import type { ScheduleResponse } from '../shared/types.js';
import { migrateSlotKey, slotKey } from '../shared/slotKey.js';

const DATA_KEY = 'pankrac-watcher-data';
const WATCHED_KEY = 'pankrac-watcher-watched';
const VISIBLE_VENUES_KEY = 'pankrac-watcher-visible-venues';
const KNOWN_VENUES_KEY = 'pankrac-watcher-known-venues';
const MY_RESERVATIONS_KEY = 'pankrac-watcher-my-reservations';
const VISIBLE_WEEKDAYS_KEY = 'pankrac-watcher-visible-weekdays';
const HOUR_RANGE_KEY = 'pankrac-watcher-hour-range';
const EMAIL_KEY = 'pankrac-watcher-email';
// Left behind by the removed change-log feature; cleared on load.
const LEGACY_CHANGES_KEY = 'pankrac-watcher-changes';

export let previousData: ScheduleResponse | null = null;
export let watchedSlots = new Set<string>();
// Purely a personal visual marker ("this is a slot I actually booked"),
// entirely separate from watchedSlots (which means "notify me when this
// frees up"). Mirrored to the server per email exactly like watchedSlots
// (see /api/reservations) — iOS evicts localStorage after ~7 days away, so
// browser storage alone kept losing these; without an email set yet they
// simply stay local until one is entered.
export let myReservations = new Set<string>();
// null means "all venues visible" (the default, before /api/venues has even
// loaded once); once loaded it's always a concrete set of venue ids.
export let visibleVenues: Set<string> | null = null;
// null means "all weekdays visible" (the default). Values are JS
// Date.getDay() numbers (0 = Sunday … 6 = Saturday). Unlike venues, the
// option set is a fixed seven, so no knownVenues-style bookkeeping needed.
export let visibleWeekdays: Set<number> | null = null;
// Which hour columns the schedule renders — always a concrete range (whole
// hours; `to` exclusive, so 17→22 shows 17:00–21:30), never derived from
// the data, so every day-table gets the same fixed column count and width.
// The default covers the full upstream opening span (7:00–22:30).
export let hourFrom = 7;
export let hourTo = 23;
// Every venue id this browser has ever seen from /api/venues — lets
// initVenueFilter (main.ts) tell "a venue that showed up since last visit"
// (should default to visible) apart from "a venue the user unchecked"
// (should stay off), which visibleVenues alone can't distinguish: both look
// like "missing from the stored set". null means never fetched yet.
export let knownVenues: Set<string> | null = null;
// venue id -> public booking page URL, from /api/venues — session-only
// (refetched on every load, so nothing to persist), used to link the
// schedule's venue/court names to the venue's real reservation site.
export let venueBookingUrls = new Map<string, string>();
// null means no email remembered yet — watching stays local-only (no server
// sync, no emails) until one is entered.
export let watcherEmail: string | null = null;

export function setWatcherEmail(email: string): void {
  watcherEmail = email;
  try {
    localStorage.setItem(EMAIL_KEY, email);
  } catch (e) {
    console.error('Chyba při ukládání emailu do local storage:', e);
  }
}

export function isSlotWatched(venueId: string, date: string, courtId: string, time: string): boolean {
  return watchedSlots.has(slotKey(venueId, date, courtId, time));
}

export function toggleWatchedSlot(venueId: string, date: string, courtId: string, time: string): void {
  const key = slotKey(venueId, date, courtId, time);
  if (watchedSlots.has(key)) {
    watchedSlots.delete(key);
  } else {
    watchedSlots.add(key);
  }
  saveToLocalStorage();
}

// Explicit set (vs. toggle) so a drag-select gesture can apply one
// consistent target state to every cell it passes over, regardless of each
// cell's individual prior state.
export function setSlotWatched(venueId: string, date: string, courtId: string, time: string, watched: boolean): void {
  const key = slotKey(venueId, date, courtId, time);
  if (watched) {
    watchedSlots.add(key);
  } else {
    watchedSlots.delete(key);
  }
}

// Mirrors the server's copy of this email's watches (see /api/watches) —
// wholesale, since the server is the source of truth.
export function replaceWatchedSlots(slots: string[]): void {
  watchedSlots = new Set(slots);
  saveToLocalStorage();
}

// Mirrors the server's copy of this email's reservation marks — wholesale,
// same contract as replaceWatchedSlots.
export function replaceMyReservations(slots: string[]): void {
  myReservations = new Set(slots);
  saveToLocalStorage();
}

export function isMyReservation(venueId: string, date: string, courtId: string, time: string): boolean {
  return myReservations.has(slotKey(venueId, date, courtId, time));
}

export function toggleMyReservation(venueId: string, date: string, courtId: string, time: string): void {
  const key = slotKey(venueId, date, courtId, time);
  if (myReservations.has(key)) {
    myReservations.delete(key);
  } else {
    myReservations.add(key);
  }
  saveToLocalStorage();
}

export function setPreviousData(data: ScheduleResponse): void {
  previousData = data;
  // Persisted here rather than by the caller so that setting new data and
  // caching it stay one step — every other setter in this file saves itself
  // too. The write is deferred and collapsed, so this costs nothing extra.
  saveSnapshotToLocalStorage();
}

export function setVisibleVenues(ids: Set<string>): void {
  visibleVenues = ids;
  saveToLocalStorage();
}

export function setVenueBookingUrls(entries: Map<string, string>): void {
  venueBookingUrls = entries;
}

export function setKnownVenues(ids: Set<string>): void {
  knownVenues = ids;
  saveToLocalStorage();
}

export function isVenueVisible(venueId: string): boolean {
  return visibleVenues === null || visibleVenues.has(venueId);
}

export function setVisibleWeekdays(days: Set<number>): void {
  visibleWeekdays = days;
  saveToLocalStorage();
}

export function isWeekdayVisible(weekday: number): boolean {
  return visibleWeekdays === null || visibleWeekdays.has(weekday);
}

export function setHourRange(from: number, to: number): void {
  hourFrom = from;
  hourTo = to;
  saveToLocalStorage();
}

// The cached snapshot is ~2 MB of JSON. Stringifying and writing it is by
// far the most expensive thing this file does, and it used to happen inside
// saveToLocalStorage() — i.e. on every single tap (watch toggle, reservation
// mark, filter change), where it blocked the main thread long enough to make
// an older iPhone look frozen. It's now split out: preferences (a few KB)
// save synchronously as before, the snapshot saves separately and only when
// new data actually arrives.
export function saveToLocalStorage(): void {
  try {
    localStorage.setItem(WATCHED_KEY, JSON.stringify([...watchedSlots]));
    localStorage.setItem(MY_RESERVATIONS_KEY, JSON.stringify([...myReservations]));
    if (visibleVenues) {
      localStorage.setItem(VISIBLE_VENUES_KEY, JSON.stringify([...visibleVenues]));
    }
    if (knownVenues) {
      localStorage.setItem(KNOWN_VENUES_KEY, JSON.stringify([...knownVenues]));
    }
    if (visibleWeekdays) {
      localStorage.setItem(VISIBLE_WEEKDAYS_KEY, JSON.stringify([...visibleWeekdays]));
    }
    localStorage.setItem(HOUR_RANGE_KEY, JSON.stringify({ from: hourFrom, to: hourTo }));
  } catch (e) {
    console.error('Chyba při ukládání do local storage:', e);
  }
}

// requestIdleCallback where available (not on older Safari), otherwise a
// plain timeout — either way the callback lands after the current frame.
const deferToIdle: (cb: () => void) => void =
  window.requestIdleCallback
    ? (cb) => window.requestIdleCallback(cb, { timeout: 2000 })
    : (cb) => { setTimeout(cb, 0); };

// Caches the current snapshot so a reload paints instantly instead of
// waiting on the network. Deferred to idle time because the write itself is
// the expensive part (see above) and nothing depends on it having finished;
// a pending write is collapsed so a burst of refreshes still writes once,
// with whatever `previousData` holds by the time it runs.
let snapshotSavePending = false;

export function saveSnapshotToLocalStorage(): void {
  if (snapshotSavePending) return;
  snapshotSavePending = true;
  deferToIdle(() => {
    snapshotSavePending = false;
    try {
      localStorage.setItem(DATA_KEY, JSON.stringify(previousData));
    } catch (e) {
      // Quota is the expected failure here (Safari caps an origin at ~5 MB)
      // — the cache is an optimization, so a failed write just means the
      // next load fetches from the network instead.
      console.warn('Snapshot se nepodařilo uložit do local storage:', e);
    }
  });
}

// A successfully JSON.parse'd blob can still be the wrong shape (an older
// cache from before a venue/schema change, or a snapshot saved mid-failed-
// fetch) — checked structurally rather than just trusting JSON.parse not to
// throw, since a malformed-but-valid-JSON cache would otherwise crash
// rendering later (deep inside groupByDate's venue.days.forEach) instead of
// failing safely here at load time.
function isValidScheduleResponse(value: unknown): value is ScheduleResponse {
  if (!value || typeof value !== 'object') return false;
  const venues = (value as { venues?: unknown }).venues;
  if (!Array.isArray(venues)) return false;
  return venues.every((v) => v && typeof v === 'object' && Array.isArray((v as { days?: unknown }).days));
}

export function loadFromLocalStorage(): boolean {
  try {
    localStorage.removeItem(LEGACY_CHANGES_KEY);

    const data = localStorage.getItem(DATA_KEY);
    const watched = localStorage.getItem(WATCHED_KEY);
    const reserved = localStorage.getItem(MY_RESERVATIONS_KEY);
    const visible = localStorage.getItem(VISIBLE_VENUES_KEY);
    const known = localStorage.getItem(KNOWN_VENUES_KEY);
    const weekdays = localStorage.getItem(VISIBLE_WEEKDAYS_KEY);
    const hours = localStorage.getItem(HOUR_RANGE_KEY);
    const email = localStorage.getItem(EMAIL_KEY);

    if (email) {
      watcherEmail = email;
    }
    if (watched) {
      watchedSlots = new Set((JSON.parse(watched) as string[]).map(migrateSlotKey));
    }
    if (reserved) {
      myReservations = new Set((JSON.parse(reserved) as string[]).map(migrateSlotKey));
    }
    if (data) {
      const parsed = JSON.parse(data);
      if (isValidScheduleResponse(parsed)) {
        previousData = parsed;
      } else {
        console.warn('Ignoruji neplatná uložená data (jiný formát než aktuální).');
        localStorage.removeItem(DATA_KEY);
      }
    }
    if (visible) {
      visibleVenues = new Set(JSON.parse(visible));
    }
    if (known) {
      knownVenues = new Set(JSON.parse(known));
    }
    if (weekdays) {
      visibleWeekdays = new Set(JSON.parse(weekdays));
    }
    if (hours) {
      // Shape-checked like the schedule blob: a stale/garbled range must
      // never leave the schedule with zero or negative columns.
      const parsed = JSON.parse(hours) as { from?: unknown; to?: unknown };
      if (typeof parsed?.from === 'number' && typeof parsed?.to === 'number' && parsed.from < parsed.to) {
        hourFrom = parsed.from;
        hourTo = parsed.to;
      }
    }

    return previousData !== null;
  } catch (e) {
    console.error('Chyba při načítání z local storage:', e);
    return false;
  }
}
